import {TailwindContents} from '@wandb/weave/components/Tailwind';
import React, {useMemo, useState} from 'react';
import {twMerge} from 'tailwind-merge';

import {useProjectParams} from '../../../pages/ProjectPage/useProjectParams';
import * as PanelBankSectionConfigTypes from '../../../state/views/panelBankSectionConfig/types';
import {isMobile} from '../../../util/panelbankFlow';
import {getPanelSpec} from '../../../util/panelTypes';
import {useFeatureFlagVegaEnabled} from '../../../util/useFeatureFlag';
import {PanelDragHandle} from '../common/PanelDragHandle';
import {PanelViewVegaSpec} from '../common/PanelViewVegaSpec';
import {PanelFullScreenButton} from '../FullScreenPanel/PanelFullScreenButton';
import {usePanelContext} from '../PanelContextProvider';
import {PanelEditButton} from '../PanelEditButton';
import {PanelRunMetricButton} from '../PanelRunMetricButton';
import {WorkspacePanelOverflowMenu} from './WorkspacePanelOverflowMenu';

export const WorkspacePanelHeader = ({
  defaultMoveToSectionRef,
}: {
  defaultMoveToSectionRef?: PanelBankSectionConfigTypes.Ref;
}) => {
  const {isReadOnly, panelBankSectionConfigRef, panelRef, panel} =
    usePanelContext();

  const panelType = panel.viewType;
  const panelSpec = useMemo(() => getPanelSpec(panelType), [panelType]);

  const {entityName} = useProjectParams();
  const vegaPanelEnabled = useFeatureFlagVegaEnabled(entityName);

  const [isOverflowMenuActive, setIsOverflowMenuActive] = useState(false);

  return (
    <TailwindContents>
      <div
        className={twMerge(
          'flex shrink-0',
          'mb-4 min-h-[24px]',
          // on a regular workspace OR report where multiple panels are visible, we want to only show
          // the actionable left side items when the mouse is over the panel. it should always be visible in full screen where we only
          // show 1 panel
          'opacity-0 hover:opacity-100',
          // since we hide the actionable items until mouse hover on a regular workspace or report where we display multiple panels,
          // we still want to show the actionable items when overflow menu is active
          isOverflowMenuActive && 'opacity-100'
        )}
        data-test="panel-header">
        {!isReadOnly && <PanelDragHandle panelRef={panelRef} />}
        <div
          className={twMerge(
            // on a regular workspace OR report where multiple panels are visible, the actions should be rendered on top
            // of the drag handle and there's no back button to consider
            'absolute right-8'
          )}>
          <div className={twMerge('flex')}>
            <PanelRunMetricButton
              entityName={entityName}
              panelBankSectionConfigRef={panelBankSectionConfigRef}
              panelRef={panelRef}
              panelType={panelType}
            />
            {isReadOnly && <PanelViewVegaSpec panelType={panelType} />}
            {!isMobile() && (
              <PanelFullScreenButton
                panelBankSectionConfigRef={panelBankSectionConfigRef}
              />
            )}
            {!isReadOnly &&
              !isMobile() &&
              !panelSpec.noEditMode &&
              (panelType !== 'Vega' || vegaPanelEnabled) && <PanelEditButton />}
            {!isReadOnly && panelBankSectionConfigRef != null && (
              <WorkspacePanelOverflowMenu
                panelSpec={panelSpec}
                panelBankSectionConfigRef={panelBankSectionConfigRef}
                defaultMoveToSectionRef={defaultMoveToSectionRef}
                isOpen={isOverflowMenuActive}
                setIsOpen={setIsOverflowMenuActive}
              />
            )}
          </div>
        </div>
      </div>
    </TailwindContents>
  );
};
